import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SortableFilterableTableHeader, ColumnFilter, ColumnSort } from '../../components/common/SortableFilterableTableHeader';
import { 
  Search, 
  Filter, 
  MoreVertical, 
  ChevronLeft, 
  ChevronRight,
  Home,
  Store,
  ChevronDown,
  Phone,
  MapPin,
  Eye,
  Star,
  Globe
} from 'lucide-react';
// Removed duplicate import of OffcanvasMenu and Header as they're provided by AppLayout
import {
  doc,
  getDoc,
  addDoc,
  Timestamp,
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  query as firestoreQuery
} from 'firebase/firestore';
import { getAssignedStores } from '../../utils/storeAssignment';
import type { Store as StoreType } from '../../@types/store';

interface Store {
  id: string;
  customerNo: string;
  name: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  phone: string;
  email: string;
  manager: string;
  type: string;
  status: 'active' | 'inactive';
}

const PageContainer = styled.div<{ isMenuCollapsed: boolean }>`
  display: flex;
  min-height: 100vh;
  background-color: #f7f9ea;
  margin-left: ${props => props.isMenuCollapsed ? '80px' : '280px'};
  transition: margin-left 0.3s ease;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 100px 20px 20px 20px;
  width: 100%;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #666;
`;

const BreadcrumbItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BreadcrumbSeparator = styled.span`
  color: #ccc;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: bold;
  color: #333;
  margin-bottom: 24px;
`;

const SearchFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  background-color: #fff;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
  
  &:focus {
    outline: none;
    border-color: #A0C93F;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.1);
    background-color: #fafafa;
  }
  
  &:hover:not(:focus) {
    border-color: #cbd5e1;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #fff;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: #f8fafc;
    border-color: #A0C93F;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    border-color: #A0C93F;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.1);
  }
`;

const TableContainer = styled.div`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid #f1f5f9;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  position: relative;
  
  &:first-child {
    padding-left: 24px;
  }
  
  &:last-child {
    padding-right: 24px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  }

  &:last-child {
    border-bottom: none;
  }

  &:focus-within {
    background-color: #f0f9ff;
    outline: 2px solid #A0C93F;
    outline-offset: -2px;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #374151;
  vertical-align: middle;
  font-weight: 400;
  
  &:first-child {
    padding-left: 24px;
    font-weight: 600;
    color: #1f2937;
  }
  
  &:last-child {
    padding-right: 24px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  color: #666;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: #f1f5f9;
    border-color: #A0C93F;
    color: #334155;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    border-color: #A0C93F;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.1);
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  min-width: 180px;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.95)'};
  transition: all 0.15s ease;
  backdrop-filter: blur(8px);
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  transition: all 0.15s ease;
  position: relative;

  &:hover {
    background-color: #f8fafc;
    color: #1f2937;
    transform: translateX(2px);
  }

  &:active {
    background-color: #f1f5f9;
    transform: translateX(0);
  }

  &:focus {
    outline: none;
    background-color: #f0f9ff;
    color: #1e40af;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:only-child {
    border-radius: 8px;
  }
`;

const DropdownIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: #666;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  background-color: ${props => props.active ? '#A0C93F' : '#fff'};
  color: ${props => props.active ? '#fff' : '#475569'};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 40px;
  position: relative;

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#8fb832' : '#f8fafc'};
    border-color: ${props => props.active ? '#8fb832' : '#A0C93F'};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    border-color: #A0C93F;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #A0C93F;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #6b7280;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyStateText = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const EmptyStateSubtext = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  max-width: 400px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: ${props => props.active ? '#fff' : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#A0C93F' : 'transparent'};
  cursor: pointer;
  font-size: 15px;
  font-weight: ${props => props.active ? '600' : '500'};
  color: ${props => props.active ? '#A0C93F' : '#6b7280'};
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: -2px;

  &:hover {
    color: #A0C93F;
    background-color: #f9fafb;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TabBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background-color: ${props => props.active ? '#A0C93F' : '#e5e7eb'};
  color: ${props => props.active ? '#fff' : '#6b7280'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
`;

type TabType = 'assigned' | 'all';

export const StoreVisitScreenDesktop = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false); // Expanded by default on desktop
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);
  const [assignedStores, setAssignedStores] = useState<Store[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('assigned');
  const isHandLead = userRole === 'hand-lead';
  const scrollContainerIdRef = useRef(`store-visit-table-${Math.random().toString(36).slice(2)}`);
  const scrollContainerId = scrollContainerIdRef.current;

  const handleMenuToggle = () => {
    setIsMenuCollapsed(!isMenuCollapsed);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  useEffect(() => {
    loadStores();
  }, [user]);

  useEffect(() => {
    if (isHandLead && assignedStores.length > 0) {
      // Update filtered stores when tab changes for hand-lead
      const sourceStores = activeTab === 'assigned' ? assignedStores : stores;
      // Filtering will be handled by filteredAndSortedStores useMemo
    }
  }, [activeTab, isHandLead, assignedStores, stores]);

  const loadStores = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const db = getFirestore();
      const storesRef = collection(db, 'Stores');
      
      const querySnapshot = await getDocs(storesRef);
      
      console.log('Total stores found:', querySnapshot.size);
      
      const storesData: Store[] = [];
      const storeTypesData: StoreType[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Skip archived (soft-deleted) stores so they don't appear in Store Visit
        if ((data.status || data.Status) === 'archived') return;
        console.log('Store data:', { id: doc.id, ...data });
        
        const store: Store = {
          id: doc.id,
          customerNo: data.CustomerNo || `CUST${doc.id.slice(-3).toUpperCase()}`,
          name: data.StoreName || 'Unknown Store',
          address: data.Address || '',
          city: data.City || '',
          province: data.Prov || '',
          postalCode: data.PostalCode || '',
          phone: data.Telephone || '',
          email: data.EmailAdddress || '',
          manager: data.SalespersonNo || '',
          type: data.SalesGroup || '',
          status: 'active'
        };
        
        storesData.push(store);
        
        // Also build StoreType array for hand-lead users
        if (isHandLead) {
          storeTypesData.push({
            id: doc.id,
            CustomerNo: store.customerNo,
            StoreName: store.name,
            Address: store.address,
            City: store.city,
            Prov: store.province,
            PostalCode: store.postalCode,
            Telephone: store.phone,
            EmailAdddress: store.email,
            SalespersonNo: store.manager,
            salesGroup: store.type,
            assignedToUserIds: data.assignedToUserIds || [],
            status: 'active',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
        }
      });
      
      console.log('Processed stores:', storesData);
      
      setStores(storesData);
      
      // For hand-lead users, also load assigned stores
      if (isHandLead && user.id) {
        const userDoc = await getDoc(doc(db, 'users', user.id));
        const userData = userDoc.data();

        const assigned = await getAssignedStores(
          user.id, 
          storeTypesData, 
          userData?.salesGroup,
          userData?.assignedStoreIds
        );
        
        const assignedData: Store[] = assigned.map(s => ({
          id: s.id,
          customerNo: s.CustomerNo || '',
          name: s.StoreName || 'Unknown Store',
          address: s.Address || '',
          city: s.City || '',
          province: s.Prov || '',
          postalCode: s.PostalCode || '',
          phone: s.Telephone || '',
          email: s.EmailAdddress || '',
          manager: s.SalespersonNo || '',
          type: s.salesGroup || '',
          status: 'active'
        }));

        console.log(`Assigned stores for hand-lead user ${user.id}:`, assignedData.length);
        setAssignedStores(assignedData);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchQuery(''); // Clear search when switching tabs
    setCurrentPage(1); // Reset to first page
    setColumnFilters([]); // Clear filters when switching tabs
    setColumnSort(null); // Clear sort when switching tabs
  };

  // Filter and sort stores based on search query, column filters, and sort
  const filteredAndSortedStores = useMemo(() => {
    // For hand-lead users, use assigned or all stores based on active tab
    const sourceStores = (isHandLead && activeTab === 'assigned') ? assignedStores : stores;
    let filtered = [...sourceStores];

    // Apply search query filter
    if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
      filtered = filtered.filter(store => 
      store.customerNo?.toLowerCase().includes(query) ||
      store.name?.toLowerCase().includes(query) ||
      store.city?.toLowerCase().includes(query) ||
      store.address?.toLowerCase().includes(query) ||
        store.email?.toLowerCase().includes(query) ||
        store.province?.toLowerCase().includes(query)
      );
    }

    // Apply column filters (supports both single value and array for multi-select)
    columnFilters.forEach(filter => {
      if (filter.value) {
        filtered = filtered.filter(store => {
          let storeValue: string = '';
          
          switch (filter.column) {
            case 'customerNo':
              storeValue = store.customerNo || '';
              break;
            case 'name':
              storeValue = store.name || '';
              break;
            case 'city':
              storeValue = store.city || '';
              break;
            case 'province':
              storeValue = store.province || '';
              break;
            case 'email':
              storeValue = store.email || '';
              break;
            case 'phone':
              storeValue = store.phone || '';
              break;
            default:
              storeValue = String((store as any)[filter.column] || '');
          }
          
          // Handle both single value and array (multi-select)
          const filterValues = Array.isArray(filter.value) ? filter.value : [filter.value];
          const storeValueLower = storeValue.toLowerCase();
          
          // Check if store value matches any of the selected filter values
          return filterValues.some(filterVal => {
            const filterValStr = String(filterVal || '').toLowerCase();
            return storeValueLower === filterValStr;
          });
        });
      }
    });

    // Apply sorting
    if (columnSort && columnSort.direction) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: string = '';
        let bValue: string = '';
        
        switch (columnSort.column) {
          case 'customerNo':
            aValue = a.customerNo || '';
            bValue = b.customerNo || '';
            break;
          case 'name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'city':
            aValue = a.city || '';
            bValue = b.city || '';
            break;
          case 'province':
            aValue = a.province || '';
            bValue = b.province || '';
            break;
          case 'email':
            aValue = a.email || '';
            bValue = b.email || '';
            break;
          case 'phone':
            aValue = a.phone || '';
            bValue = b.phone || '';
            break;
          default:
            aValue = String((a as any)[columnSort.column] || '');
            bValue = String((b as any)[columnSort.column] || '');
        }
        
        if (columnSort.direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });
    }

    return filtered;
  }, [stores, assignedStores, isHandLead, activeTab, searchQuery, columnFilters, columnSort]);

  // Handle filter change
  const handleFilterChange = useCallback((filters: ColumnFilter[]) => {
    setColumnFilters(filters);
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  // Handle sort change
  const handleSortChange = useCallback((sort: ColumnSort | null) => {
    setColumnSort(sort);
    setCurrentPage(1); // Reset to first page when sort changes
  }, []);

  const handleStoreAction = (store: Store, action: string) => {
    console.log(`${action} clicked for store:`, store);
    setOpenDropdown(null); // Close dropdown after action
    
    switch (action) {
      case 'details':
        navigate(`/manager/store-details/${store.id}?tab=store-visit`);
        break;
      case 'call':
        if (store.phone) {
          // Open phone dialer in new tab/window to keep user on current screen
          window.open(`tel:${store.phone}`, '_blank');
        }
        break;
      case 'visit':
        if (store.address) {
          const encodedAddress = encodeURIComponent(`${store.address}, ${store.city}, ${store.province}`);
          // Open Google Maps in new tab to keep user on current screen
          window.open(`https://maps.google.com/maps?q=${encodedAddress}`, '_blank');
        }
        break;
      default:
        break;
    }
  };

  const handleRowClick = (store: Store) => {
    navigate(`/manager/store-details/${store.id}?tab=store-visit`);
  };

  const handleDropdownToggle = (storeId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setOpenDropdown(openDropdown === storeId ? null : storeId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };

    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedStores.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStores = filteredAndSortedStores.slice(startIndex, endIndex);

  // Define columns for SortableFilterableTableHeader
  const tableColumns = [
    { key: 'customerNo', label: 'Customer No', filterable: true, sortable: true },
    { key: 'name', label: 'Store Name', filterable: true, sortable: true },
    { key: 'address', label: 'Address', filterable: false, sortable: true },
    { key: 'city', label: 'City', filterable: true, sortable: true },
    { key: 'province', label: 'Province', filterable: true, sortable: true },
    { key: 'postalCode', label: 'Postal Code', filterable: false, sortable: true },
    { key: 'email', label: 'Contact E-mail', filterable: true, sortable: true },
    { key: 'phone', label: 'Phone Number', filterable: true, sortable: true },
    { key: 'actions', label: 'Actions', filterable: false, sortable: false }
  ];

  // Prepare table data for filter/sort component
  const tableData = useMemo(() => {
    const sourceStores = (isHandLead && activeTab === 'assigned') ? assignedStores : stores;
    return sourceStores.map(store => ({
      customerNo: store.customerNo || '',
      name: store.name || '',
      address: store.address || '',
      city: store.city || '',
      province: store.province || '',
      postalCode: store.postalCode || '',
      email: store.email || '',
      phone: store.phone || ''
    }));
  }, [stores, assignedStores, isHandLead, activeTab]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <PaginationButton
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i.toString().padStart(2, '0')}
          </PaginationButton>
        );
      }
    } else {
      // Show first page
      pages.push(
        <PaginationButton
          key={1}
          active={1 === currentPage}
          onClick={() => handlePageChange(1)}
        >
          01
        </PaginationButton>
      );

      // Show current page and surrounding pages
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      if (startPage > 2) {
        pages.push(<span key="ellipsis1">...</span>);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationButton
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i.toString().padStart(2, '0')}
          </PaginationButton>
        );
      }

      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2">...</span>);
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(
          <PaginationButton
            key={totalPages}
            active={totalPages === currentPage}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages.toString().padStart(2, '0')}
          </PaginationButton>
        );
      }
    }

    return pages;
  };

  if (loading) {
    return (
      <PageContainer isMenuCollapsed={isMenuCollapsed}>
        <MainContent>
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading stores...</LoadingText>
          </LoadingContainer>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer isMenuCollapsed={isMenuCollapsed}>
        <MainContent>
          <BreadcrumbContainer>
            <BreadcrumbItem>
              <Home size={16} />
              Home
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <Store size={16} />
              Stores
            </BreadcrumbItem>
          </BreadcrumbContainer>

          <PageTitle>Stores</PageTitle>

          {/* Tabs for hand-lead users - show assigned vs all stores */}
          {isHandLead && (
            <TabContainer>
              <Tab 
                active={activeTab === 'assigned'} 
                onClick={() => handleTabChange('assigned')}
              >
                <Star size={18} />
                {t('storeVisit.myStores', 'My Stores')}
                <TabBadge active={activeTab === 'assigned'}>
                  {assignedStores.length}
                </TabBadge>
              </Tab>
              <Tab 
                active={activeTab === 'all'} 
                onClick={() => handleTabChange('all')}
              >
                <Globe size={18} />
                {t('storeVisit.allStores', 'All Stores')}
                <TabBadge active={activeTab === 'all'}>
                  {stores.length}
                </TabBadge>
              </Tab>
            </TabContainer>
          )}

          <SearchFilterContainer>
            <SearchContainer>
              <SearchIcon>
                <Search size={20} />
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search by customer#, store name, city, or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
          </SearchFilterContainer>

          {loading ? (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>Loading stores...</LoadingText>
            </LoadingContainer>
          ) : filteredAndSortedStores.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <Store size={48} />
              </EmptyStateIcon>
              <EmptyStateText>No stores found</EmptyStateText>
              <EmptyStateSubtext>
                {searchQuery || columnFilters.length > 0 ? 'Try adjusting your search terms or filters' : 'No stores available at the moment'}
              </EmptyStateSubtext>
            </EmptyState>
          ) : (
            <>
              <TableContainer data-scroll-container={scrollContainerId}>
                <Table>
                  <TableHeader>
                    <tr>
                      <SortableFilterableTableHeader
                        scrollContainerId={scrollContainerId}
                        columns={tableColumns}
                        data={tableData}
                        onFilterChange={handleFilterChange}
                        onSortChange={handleSortChange}
                        currentFilters={columnFilters}
                        currentSort={columnSort}
                        renderAsRow={true}
                      />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {currentStores.map((store) => (
                      <TableRow key={store.id} onClick={() => handleRowClick(store)}>
                        <TableCell>{store.customerNo}</TableCell>
                        <TableCell>{store.name}</TableCell>
                        <TableCell>{store.address}</TableCell>
                        <TableCell>{store.city}</TableCell>
                        <TableCell>{store.province}</TableCell>
                        <TableCell>{store.postalCode}</TableCell>
                        <TableCell>{store.email}</TableCell>
                        <TableCell>{store.phone}</TableCell>
                        <TableCell>
                          <DropdownContainer>
                            <DropdownButton
                              onClick={(e) => handleDropdownToggle(store.id, e)}
                              title="Actions"
                            >
                              Actions
                              <ChevronDown size={14} />
                            </DropdownButton>
                            <DropdownMenu isOpen={openDropdown === store.id}>
                              <DropdownItem onClick={() => handleStoreAction(store, 'details')}>
                                <DropdownIcon>
                                  <Eye size={16} />
                                </DropdownIcon>
                                Details
                              </DropdownItem>
                              <DropdownItem onClick={() => handleStoreAction(store, 'call')}>
                                <DropdownIcon>
                                  <Phone size={16} />
                                </DropdownIcon>
                                Call
                              </DropdownItem>
                              <DropdownItem onClick={() => handleStoreAction(store, 'visit')}>
                                <DropdownIcon>
                                  <MapPin size={16} />
                                </DropdownIcon>
                                Visit
                              </DropdownItem>
                            </DropdownMenu>
                          </DropdownContainer>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </PaginationButton>
                  {renderPagination()}
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={16} />
                  </PaginationButton>
                </PaginationContainer>
              )}
            </>
          )}
        </MainContent>
      </PageContainer>
  );
};
